import { Graph, IG6GraphEvent, EdgeConfig, Modes } from '@antv/g6';
import isEmpty from 'lodash/isEmpty';

import { GraphMode, itemStateDataMap, ItemState, GraphBehavior } from '../constants';
import { getChildNodeGroupFromEvent, getChildNodeDataFromGroup } from '../shapes';
import {
  EdgeModel,
  Item,
  ItemArg,
  ItemData,
  ItemMap,
  ModeType,
  Node,
  NodeId,
  NodeModel,
} from '../types';
import { getSelectedNodes, getSelectedEdges, isNode } from './graph';

type NodeOrNodeModel = Node | NodeModel;
type ItemParams = Record<string, any>;
type ItemOrItemModel = Item | NodeModel | EdgeModel | ItemParams;

/** Is the incoming arg an id? */
export const isId = (item: ItemArg): item is string => typeof item === 'string';

/**
 * Get the item if you only have id.  Otherwise, return item.
 */
export const getItem = (graph: Graph, item: ItemArg): Item =>
  isId(item) ? graph?.findById(item as string) : item;

/**
 * Gets the json which you originally feed into the node or edge
 * Good for when you want to make generic functions that can apply to either the
 * graph node/edge or the node/edge input data
 */
export const getModel = (item?: ItemOrItemModel | null) => {
  if (isEmpty(item)) return;
  if (typeof (item as Item)?.getModel === 'function') {
    return (item as Item).getModel();
  }
  // `.model` is used in some of the commands
  return !isEmpty((item as ItemParams).model) ? (item as ItemParams).model : item;
};

/**
 * Normalizes the node data into something that makes sense for our application
 */
export const normalizeItemData = (item?: ItemOrItemModel | null): ItemData | undefined => {
  const model = getModel(item);
  if (!model) return;
  const { id, type: itemType } = model;
  if (!id) return;

  // Depending on where you're getting the item from, the data might be on a
  // param `model`, or directly on the value passed in.
  // For this reason, several places have to be checked for accuracy.
  const data = Object.entries(model || item || {}).reduce(
    (acc, [key, value]) =>
      // removed unused params from object
      ['size', 'style'].includes(key) ? acc : { ...acc, [key]: value },
    {},
  );

  return { id, data, type: itemType };
};

/** Normalizes an array of items, removing any that are invalid */
export const normalizeItemArray = (items: ItemOrItemModel[] = []): ItemData[] =>
  items.reduce((acc: ItemData[], item) => {
    const data = normalizeItemData(item);
    return data ? [...acc, data] : acc;
  }, []);

/**
 * Normalizes a map of items, removing any that are invalid.
 * NOTE: This will return an array, not a map
 */
export const normalizeItemMap = (itemMap: ItemMap = {}): ItemData[] =>
  normalizeItemArray(Object.values(itemMap));

/**
 * Checks if the item has been destroyed
 * Since there are essentially two sources of truth (the graph and redux), it is
 * necessary to check this before performing a set of functionality, as the item
 * could have been removed through either store
 */
const isItemDestroyed = (item?: Item) => !item || item?.destroyed !== false || !item?._cfg;

/**
 * Safely parses an array of item data for callbacks, and removes any items
 * which have been destroyed
 */
const parseItemArray = (items: Item[] = []) =>
  items.reduce((acc: ItemData[], item) => {
    if (isItemDestroyed(item)) return acc;
    const data = normalizeItemData(item);
    return data ? [...acc, data] : acc;
  }, []);

/**
 * Normalizes the data from graph events
 */
export const normalizeNodeEventData = (e: IG6GraphEvent): ItemData | undefined => {
  const { item } = e;
  const data = normalizeItemData(item);

  if (data && item) {
    const childNodeGroup = getChildNodeGroupFromEvent(e);
    const childNodeData = getChildNodeDataFromGroup(childNodeGroup, item);
    return { ...data, childNodeData: childNodeData.data, childNodeIndex: childNodeData.index };
  }
};

/**
 * Get the nodes currently selected
 */
export const getSelectedNodesData = (graph: Graph) => parseItemArray(getSelectedNodes(graph));

/**
 * Get the edges currently selected
 */
export const getSelectedEdgesData = (graph: Graph) => parseItemArray(getSelectedEdges(graph));

/**
 * Get the edges for a particular node
 */
export const getNodeEdgesData = (graph: Graph, id: NodeId) => {
  if (!id) return [];
  const item = getItem(graph, id);
  if (isItemDestroyed(item) || !isNode(item)) return [];
  return parseItemArray(item.getEdges() || []);
};

/**
 * Get all nodes
 */
export const getNodes = (graph?: Graph) => parseItemArray(graph?.getNodes() || []);

/**
 * Get all edges
 */
export const getEdges = (graph?: Graph) => parseItemArray(graph?.getEdges() || []);

/**
 * Is the item in question within the flowchart?
 */
export const doesItemExist = (graph: Graph, i?: ItemArg | null): boolean =>
  !i ? false : getItem(graph, i)?.destroyed === false;

/**
 * Can this node be cloned?
 * Defaults to true, so checks if `canClone` is explicitly set to false
 */
export const canCloneNode = (node: NodeOrNodeModel) =>
  isEmpty(node) ? false : getModel(node).canClone !== false;

/**
 * Checks if any nodes in an array of nodes can be cloned.
 */
export const canAnyNodeBeCloned = (nodes: NodeOrNodeModel[] = []): boolean =>
  !!nodes.length && nodes.some(canCloneNode);

/**
 * Filter an array of nodes and return only those nodes that can be cloned.
 */
export const getClonableNodes = (nodes: NodeOrNodeModel[] = []): NodeOrNodeModel[] =>
  nodes.reduce((acc: NodeOrNodeModel[], node) => (canCloneNode(node) ? [...acc, node] : acc), []);

/**
 * Can this node be deleted?
 * Defaults to true, so checks if `canDelete` is explicitly set to false
 */
export const canDeleteNode = (node: NodeOrNodeModel) =>
  isEmpty(node) ? false : getModel(node).canDelete !== false;

/**
 * Checks if any nodes in an array of nodes can be deleted.
 */
export const canAnyNodeBeDeleted = (nodes: NodeOrNodeModel[] = []): boolean =>
  !!nodes.length && nodes.some(canDeleteNode);

/**
 * Filter an array of nodes and return only those nodes that can be deleted.
 */
export const getDeletableNodes = (nodes: NodeOrNodeModel[] = []): NodeOrNodeModel[] =>
  nodes.reduce((acc: NodeOrNodeModel[], node) => (canDeleteNode(node) ? [...acc, node] : acc), []);

/**
 * Is the graph editable?
 */
export const isGraphEditable = (graph?: Graph | null) =>
  graph &&
  ![GraphMode.Readonly, GraphMode.JourneyAssistActive].includes(
    graph.getCurrentMode() as GraphMode,
  );

/**
 * Get the data value associated with a state from the model
 */
export const getModelStateDataValue = (model: NodeModel | EdgeConfig, state: ItemState) => {
  const dataProp = itemStateDataMap[state];
  return (model?.nodeData as Record<string, any>)?.[dataProp as string] || model[dataProp];
};

/**
 * Determines whether an item should show a given state dependent on whether
 * the state exists or the model contains the associated data
 */
export const shouldItemShowState = (item: Item, state: ItemState) => {
  if (!item) return false;
  return item.hasState(state) || getModelStateDataValue(item.getModel(), state) || false;
};

/**
 * Gets the behaviors for the current mode
 */
export const getBehaviors = (graph: Graph) => {
  const modes: Modes = graph.get('modes') || [];
  return modes[graph.getCurrentMode()];
};

/**
 * Check if a behavior is a certain type (or name)
 */
export const isBehaviorType = (behavior: ModeType, desiredBehaviorType: GraphBehavior | string) => {
  if (isEmpty(behavior)) return false;
  const currentBehaviorType = typeof behavior === 'string' ? behavior : behavior?.type;
  return currentBehaviorType === desiredBehaviorType;
};

/**
 * Checks if the current state has a particular behavior
 */
export const isBehaviorEnabled = (graph: Graph, behaviorName: string) => {
  if (!graph || isEmpty(behaviorName)) return false;

  const behaviors = getBehaviors(graph);

  return behaviors ? behaviors.some((behavior) => isBehaviorType(behavior, behaviorName)) : false;
};

/** Is this the first node in a tree? */
export const isHeadNode = (node: Node) =>
  node.getInEdges().length === 0 && node.getOutEdges().length > 0;
